<template>
  <div class="home-page">
    <NewsViewModal :isVisible="showNews" @close="showNews = false" />

    <!-- ✅ 橫幅區塊（原本的內容） -->
    <div class="home-banner banner-control">
      <div class="slider">
        <div
          class="slide"
          v-for="(slide, index) in slides"
          :key="index"
          :class="{ active: index === current }"
        >
          <img
            :src="slide.image"
            :alt="slide.title"
            @error="handleBannerImgError"
            @load="handleImageLoad($event, index)"
          />
          <div class="overlay"></div>
        </div>
      </div>

      <!-- 下方箭頭與圓點 -->
      <button class="arrow left" @click="prevSlide">❮</button>
      <div class="dots">
        <span
          v-for="(slide, index) in slides"
          :key="index"
          :class="{ active: index === current }"
          @click="goToSlide(index)"
        ></span>
      </div>
      <button class="arrow right" @click="nextSlide">❯</button>
    </div>

    <!-- ✅ 公司介紹與品牌理念區塊 -->
    <section class="about-section">
      <div class="about-container">
        <!-- 整合進同一個大型理念卡片中 -->
        <div class="philosophy-card combined-layout">
          <!-- 左側：公司介紹 -->
          <div class="card-column intro-col">
            <h2 class="about-title">公司介紹</h2>
            <div class="about-description">
              <p>
                安裕和國際企業社，致力於提供安心、安全、值得信賴的動物醫療相關產品，陪伴獸醫師在每一個臨床現場，穩定地完成重要的醫療判斷與照護工作。
              </p>
              <p>
                我們始終相信，動物醫療並非只關乎技術與設備，而是一條由獸醫師、小動物，到陪伴在側的家長所共同構成的照護鏈。
                因此，安裕和在選擇與引進產品時，不只關注功能與規格，更在意實際使用過程中，是否能讓每一個角色都感到安心。
              </p>
              <p>
                對獸醫師而言，產品是否順手、穩定、可信賴，直接影響專業能否被完整發揮；
                對小動物而言，材質與設計的細節，能讓牠們在治療與照護過程中感受更少的不適與恐懼；
                而當小動物能夠感到舒適，陪伴牠們的家長，內心也才能真正放下焦慮，感到安心與放心。
              </p>
              <p>
                這份由內而外延伸的安心感，正是安裕和希望透過每一項嚴選產品，傳遞給醫療現場的核心價值。
              </p>
              <p>
                安裕和不追求長期利益與價格競逐，而選擇與獸醫師及廠商建立長期、透明、互信的合作關係。
                我們相信，當專業被尊重、情感被理解、合作建立在信任之上，真正的富裕，便不只是數字，而是一種能夠長久維持的安心與祥和。
              </p>
            </div>
          </div>

          <!-- 右側：品牌理念 -->
          <div class="card-column philosophy-col">
            <h3 class="philosophy-title">品牌理念</h3>
            <div class="philosophy-content">
              <div class="main-vision">
                我們的理念是——<br />
                <span class="vision-highlight">
                  使用安心安全，<br />
                  共創動物醫師的心靈富裕，<br />
                  並在現實中，與合作夥伴一同走向富裕與祥和。
                </span>
              </div>
              <div class="philosophy-details">
                <p>
                  以安心、安全為本，嚴選每一項動物醫療產品，讓獸醫師使用順手、讓小動物感受舒適，也讓陪伴在側的家長，能真正放下心來。
                </p>
                <p>
                  我們相信，真正好的醫療，不只完成治療，更能讓專業被尊重、情感被安放，並在信任中，與夥伴共創心靈與現實的富裕與祥和。
                </p>
                <p>
                  我們期待的，從來不只是一次性的交易，而是與夥伴一起，打造一個安心、安全、可長可久的動物醫療環境。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 🔽 接在 about-section 後面 -->
    <section class="hot-products">
      <h2 class="section-title">熱門產品</h2>

      <div class="product-slider">
        <!-- 滾動容器 -->
        <div
          class="product-track"
          :class="{ 'no-transition': !isTransitioning }"
          :style="productTrackStyle"
        >
          <div
            v-for="(item, index) in products"
            :key="index"
            class="product-card"
            @click="goProductDetail(item.id)"
          >
            <img
              :src="item.image"
              alt="熱門產品"
              class="product-img"
              @error="handleImgError"
            />
            <div class="product-info">
              <h3>{{ item.title }}</h3>
              <p class="product-desc">{{ item.desc }}</p>
              <div class="product-buttons">
                <button class="btn-primary" @click="goProductDetail(item.id)">
                  查看詳情 →
                </button>
              </div>
            </div>
          </div>
          <!-- ✅ clone 第一張 -->
          <div class="product-card" v-if="products.length > 0">
            <img
              :src="products[0].image"
              class="product-img"
              @error="handleImgError"
            />
            <div class="product-info">
              <h3>{{ products[0].title }}</h3>
              <p>{{ products[0].desc }}</p>
              <div class="product-buttons">
                <button class="btn-primary">查看詳情 →</button>
                <!-- <button class="btn-outline">了解更多 ⓘ</button> -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 🔽 這裡放進 product-slider 下方 -->
      <div class="product-dots">
        <span
          v-for="(dot, index) in productDots"
          :key="index"
          :class="{ active: index === currentProduct % products.length }"
          @click="goToProduct(index)"
        ></span>
      </div>

      <!-- 下方查看更多按鈕 + 左右箭頭 -->
      <div class="view-all control-row">
        <button class="arrow-btn left" @click="prevProduct">❮</button>
        <button class="btn-view-all" @click="goToProducts">查看所有產品</button>
        <button class="arrow-btn right" @click="nextProduct">❯</button>
      </div>
    </section>

    <section class="faq-section">
      <h2 class="section-title">常見問題</h2>

      <div class="faq-container">
        <div
          v-for="(item, index) in faqs"
          :key="index"
          class="faq-item"
          :class="{ active: activeFAQ === index }"
          @click="toggleFAQ(index)"
        >
          <div class="faq-question">
            <span>{{ item.question }}</span>
            <span class="faq-arrow">{{ activeFAQ === index ? "▲" : "▼" }}</span>
          </div>

          <!-- ✅ 新增 transition 動畫 -->
          <transition name="faq-smooth">
            <div
              v-if="activeFAQ === index"
              class="faq-answer"
              v-html="item.answer"
            ></div>
          </transition>
        </div>
      </div>
    </section>
  </div>
</template>

<script src="@/assets/js/home.js" defer></script>

<style scoped src="@/assets/css/home.css"></style>
